import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import { api, type AccountRow } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/Skeletons';

interface CreateAccountPayload {
  display_name: string;
  subdomain: string;
  access_token: string;
}

export function Accounts(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [form, setForm] = useState<CreateAccountPayload>({
    display_name: '',
    subdomain: '',
    access_token: '',
  });
  const queryClient = useQueryClient();

  const { data = [], isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<AccountRow[]>('/admin/accounts'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateAccountPayload) =>
      api.post<AccountRow>('/admin/accounts', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setIsOpen(false);
      setForm({ display_name: '', subdomain: '', access_token: '' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/accounts/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts'] }),
  });

  const copy = (url: string): void => {
    void navigator.clipboard.writeText(url);
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Kommo accounts</h1>
          <p className="text-sm text-muted-foreground">
            Подключённые аккаунты Kommo и их webhook-адреса
          </p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4" />
              Добавить аккаунт
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate(form);
              }}
              className="space-y-4"
            >
              <DialogHeader>
                <DialogTitle>Новый Kommo-аккаунт</DialogTitle>
                <DialogDescription>
                  Создайте приватную интеграцию в Kommo и вставьте long-lived access token.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <Label htmlFor="display_name">Название</Label>
                <Input
                  id="display_name"
                  placeholder="ClientX — основная воронка"
                  value={form.display_name}
                  onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subdomain">Subdomain</Label>
                <Input
                  id="subdomain"
                  placeholder="clientx.kommo.com"
                  value={form.subdomain}
                  onChange={(e) => setForm({ ...form, subdomain: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="access_token">Access token</Label>
                <div className="relative">
                  <Input
                    id="access_token"
                    type={showToken ? 'text' : 'password'}
                    placeholder="eyJ0eXAi…"
                    value={form.access_token}
                    onChange={(e) => setForm({ ...form, access_token: e.target.value })}
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {createMutation.error && (
                <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                  {(createMutation.error as Error).message}
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Создаём…' : 'Создать'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      {isLoading ? (
        <TableSkeleton columns={['Название', 'Subdomain', 'Webhook URL', '']} rows={3} />
      ) : (
      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Название</TableHead>
              <TableHead>Subdomain</TableHead>
              <TableHead>Webhook URL</TableHead>
              <TableHead className="w-12 text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {!isLoading && data.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                  Аккаунтов пока нет. Добавьте первый.
                </TableCell>
              </TableRow>
            )}
            {data.map((account) => (
              <TableRow key={account.id}>
                <TableCell className="font-medium">{account.display_name}</TableCell>
                <TableCell>
                  <code className="rounded bg-muted px-2 py-0.5 text-xs">{account.subdomain}</code>
                </TableCell>
                <TableCell>
                  <div className="flex max-w-md items-center gap-2">
                    <code className="flex-1 truncate rounded bg-muted px-2 py-0.5 text-xs">
                      {account.webhook_url}
                    </code>
                    <Button variant="ghost" size="icon" onClick={() => copy(account.webhook_url)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      if (
                        window.confirm(`Удалить аккаунт "${account.display_name}"? Pipelines, использующие его, тоже потребуют пересоздания.`)
                      ) {
                        deleteMutation.mutate(account.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      )}
    </div>
  );
}
